import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { Color, Label } from 'ng2-charts';

import { ChartDataSets, ChartOptions, ChartType } from 'chart.js';
import {HEALTH_DATA_SLEEP_VALUES, HEALTH_DATA_VALUES} from '../../../../core/dashboard-constants';
import { HealthData, HealthDataType } from '../../../../models/health-data';

export interface MetricsGraphData {
  healthData?: HealthData;
  healthDataType?: HealthDataType;
}

export const GRAPH_BACKGROUND_COLOR = 'rgba(0,77,64,0.8)';
export const GRAPH_BORDER_COLOR = 'rgba(0,77,64,1)';
export const GRAPH_LINE_COLOR = 'rgba(0,255,0,1)';

@Component({
  selector: 'dashboard-metrics-image-dialog',
  templateUrl: './metrics-dialog.component.html',
  styleUrls: ['./metrics-dialog.component.scss'],
})
export class MetricsDialogComponent implements OnInit {
  imageSrc = '';
  graphData = null;

  graphLabels: Label[] = [];
  graphDataset: ChartDataSets[] = [{data: []}];
  graphType: ChartType;
  graphColors: Color[] = [
    {
      backgroundColor: GRAPH_BACKGROUND_COLOR,
      borderColor: GRAPH_BORDER_COLOR,
    },
  ];
  graphDataToShow = [];
  graphOptions: ChartOptions = {
    legend: {
      display: false,
    },
    tooltips: {
      enabled: false,
    },
    hover: {
      mode: null,
    },
  };

  heartRateGraphOptions: ChartOptions = {
    ...this.graphOptions,
    events: ['click'],
    scales: {
      xAxes: [
        {
          position: 'bottom',
          gridLines: {
            zeroLineColor: GRAPH_LINE_COLOR,
          },
          ticks: {
            callback(value: number | string): string {
              return `${new Date(value).getHours().toLocaleString('en-GB', {
                minimumIntegerDigits: 2,
                useGrouping: false,
              })}:${new Date(value).getMinutes().toLocaleString('en-GB', {
                minimumIntegerDigits: 2,
                useGrouping: false,
              })}`;
            },
          },
          scaleLabel: {
            display: true,
            labelString: 'time',
          },
        },
      ],
      yAxes: [
        {
          position: 'left',
          gridLines: {
            zeroLineColor: GRAPH_LINE_COLOR,
          },
          scaleLabel: {
            display: true,
            labelString: 'heart rate',
          },
        },
      ],
    },
  };

  stepsGraphOptions: ChartOptions = {
    ...this.graphOptions,
    elements: {
      point: {
        radius: 0,
      },
    },
    scales: {
      xAxes: [
        {
          position: 'bottom',
          gridLines: {
            zeroLineColor: GRAPH_LINE_COLOR,
          },
          scaleLabel: {
            display: true,
            labelString: 'time(h)',
          },
        },
      ],
      yAxes: [
        {
          position: 'left',
          scaleLabel: {
            display: true,
            labelString: 'steps (count)',
          },
        },
      ],
    },
  };

  sleepGraphOption: ChartOptions = {
    ...this.graphOptions,
    scales: {
        xAxes: [
          {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'HH:mm',
              }
            },
            position: 'bottom',
            scaleLabel: {
              display: true,
              labelString: 'time',
            },
          },
        ],
        yAxes: [
          {
            position: 'left',
            ticks: {
              beginAtZero: true,
              max: 8,
              min: 0,
              callback(value , index) {
                if (index === 0 || index === 8) {
                  return '';
                } else {
                  value = Object.keys(HEALTH_DATA_SLEEP_VALUES)[index - 1];
                  return value;
                }
              }
            }
          },
        ],
       },
  };

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { imageSrc: string; graphData: MetricsGraphData }
  ) {}

  ngOnInit(): void {
    this.imageSrc = this.data?.imageSrc;
    this.graphData = this.data?.graphData;

    if (this.imageSrc) {
      return;
    }

    if (this.graphData.healthDataType.value === HEALTH_DATA_VALUES.SLEEP) {
      this.graphDataset = [
        {
          label: this.graphData.healthDataType?.translation,
          data: this.graphDataToShow,
          categoryPercentage: 1,
          barPercentage: 1,
          barThickness: 'flex'
        },
      ];
    } else {
      this.graphDataset = [
        {
          label: this.graphData.healthDataType?.translation,
          data: this.graphDataToShow,
        },
      ];
    }

    this.graphData?.healthData[this.graphData.healthDataType.value]?.forEach((healthValue) => {
      if (this.graphData.healthDataType.value === HEALTH_DATA_VALUES.HEART_RATE) {
        this.graphDataToShow.push({ y: healthValue.value, x: new Date(healthValue.date)});
      } else if (this.graphData.healthDataType.value === HEALTH_DATA_VALUES.SLEEP) {
        this.graphDataToShow.push(this.getSleepValue(healthValue.value));
        this.graphLabels.push(healthValue.startDate);
      } else {
        this.graphDataToShow.push(healthValue.value);
        this.graphLabels.push(new Date(healthValue.startDate).getHours().toString());
      }
    });

    if (this.graphData?.healthDataType?.value === HEALTH_DATA_VALUES.HEART_RATE) {
      this.graphType = 'scatter';
      this.graphOptions = this.heartRateGraphOptions;
    } else if (this.graphData?.healthDataType?.value === HEALTH_DATA_VALUES.SLEEP) {
      this.graphType = 'bar';
      this.graphOptions = this.sleepGraphOption;
    } else {
      this.graphType = 'bar';
      this.graphOptions = this.stepsGraphOptions;
    }
  }

  getSleepValue(value: string): number {
    let sleepValue;
    switch (value) {
      case 'sleep.outofbed':
        sleepValue = 7;
        break;
      case 'sleep.inbed':
        sleepValue = 6;
        break;
      case 'sleep.awake':
        sleepValue = 5;
        break;
      case 'sleep.asleep':
        sleepValue = 4;
        break;
      case 'sleep.rem':
        sleepValue = 3;
        break;
      case 'sleep.light':
        sleepValue = 2;
        break;
      case 'sleep.deep':
        sleepValue = 1;
        break;
      default:
        sleepValue = value
        break;
    }
    return sleepValue;
  }
}
